import { FieldMetadata, FieldWidthEnum, NavigationCommandMetadata } from '../../../cer/cer-data/cer-data-types';
import { UiCommandEvent } from '@ui/cer-ui-types';
import { CerFormAdapter } from '@form/cer-form-adapter';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView } from '@form/cer-form-view';

import { ProductService, uiCmdSyncSelected } from '../product.service';
import { CerFormRouteOptions } from '@form/cer-form-route-options';

type StatMode = 'RefDataProductStat' | 'RefDataProductStatCategory' | 'RefDataProductStatPeriod';

export const productNavigations: NavigationCommandMetadata[] =[
        { link: ['navMenu', 'fk'], linkMenu: 'Stamdata', title: 'Kontrakt', foreignKey: { parentEntity: 'ContractLegacy', parentColumn: 'hkontraktStr', column: 'contractNum' } },
        { link: 'navMenu', linkMenu: 'Stamdata', foreignKey: { parentEntity: 'AfregningsTabel', parentColumn: 'hid', column: 'projectLegacyId' } },
        { link: 'navMenu', linkMenu: 'Stamdata', foreignKey: { parentEntity: 'RefDataProductInbound', parentColumn: 'productISBN', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Økonomi', title: 'Nøgletal', foreignKey: { parentEntity: 'RefDataProductStat', parentColumn: 'productNum', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Økonomi', title: 'Nøgletal pr. art', foreignKey: { parentEntity: 'RefDataProductStatCategory', parentColumn: 'productNum', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Økonomi', title: 'Nøgletal perioder', foreignKey: { parentEntity: 'RefDataProductStatPeriod', parentColumn: 'productNum', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Økonomi', title: 'Kostpriser', foreignKey: { parentEntity: 'WarehouseOnhandCostJournalLineView', parentColumn: 'productNum', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Økonomi', foreignKey: { parentEntity: 'FinanceProjectTrans', parentColumn: 'projectNum', column: 'projectNum' } },
        { link: 'navMenu', linkMenu: 'Økonomi', foreignKey: { parentEntity: 'VoucherPostingProject', parentColumn: 'projectNum', column: 'projectNum' } },
        { link: 'navMenu', linkMenu: 'Lager', foreignKey: { parentEntity: 'WarehouseOnhandStatus', parentColumn: 'product', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Lager', paneTarget: 'right', foreignKey: { parentEntity: 'WarehousePurchLine', parentColumn: 'productNum', column: 'num' } },
        { link: 'navMenu', linkMenu: 'Kontrakt', title: 'Royalty, status', paneTarget: 'bottom', foreignKey: { parentEntity: 'RoyaltyAfregningTransAktuelView', parentColumn: 'hovedkontrakt', column: 'contractNum' } }
      ];

export class RefDataProductForm extends CerFormAdapter {

  private productService: ProductService;

  override viewSetInit(viewSet: CerFormViewSet) {

    this.productService = this.context.injector.get(ProductService);

    const view = this.viewGet();
    viewSet.viewAdd(view);
  }

  private viewGet(): CerFormView {
    const view = new CerFormView();

    view.onCommandEvent = this.onCommand.bind(this);

    view.viewMetadata = {
      name: 'titler', dataApiName: 'RefDataProduct', text: 'Titler', textSingular: 'Titel',
      primaryKey: 'num', allowEdit: false, allowCreate: false, allowDelete: false,
      navigations: productNavigations
    };

    view.fieldMetadata =
      [{ name: 'id', text: 'Id', tooltipText: 'Unik nøgle for posten', format: 'Number', widthType: FieldWidthEnum.XS, visible: false },
      { name: 'num', text: 'ISBN', fieldType: 'productLookup' },
      { name: 'projectNum', text: 'Projekt', widthType: FieldWidthEnum.XS },
      { name: 'projectNumParent', text: 'Overprojekt', widthType: FieldWidthEnum.XS, visible: false },
      { name: 'contractNum', text: 'Kontrakt', widthType: FieldWidthEnum.XS, visible: false },
      { name: 'name', text: 'Titel', tooltipText: 'Titel', widthType: FieldWidthEnum.XXL },
      { name: 'nameSub', text: 'Undertitel', widthType: FieldWidthEnum.XL, visible: false },
      { name: 'languageOriginal', text: 'Orig.sprog', fieldType: 'language', tooltipText: 'Originalsprog', visible: false },
      { name: 'nameOriginal', text: 'Orig.titel', tooltipText: 'Originaltitel', widthType: FieldWidthEnum.L, visible: true },
      { name: 'nameOriginalSub', text: 'Orig.undertitel', tooltipText: 'Originalundertitel', widthType: FieldWidthEnum.L, visible: false },
      { name: 'nameWorking', text: 'Arb.titel', tooltipText: 'Arbtitel', widthType: FieldWidthEnum.L, visible: false },
      { name: 'contributorAuthor', text: 'Forfatter', tooltipText: 'Forfatter(e)', widthType: FieldWidthEnum.M, visible: false },
      { name: 'responsibleShortName', text: 'Redaktør', widthType: FieldWidthEnum.XXS, foreignKeyType: 'userShortName' },
      { name: 'editionNumber', text: 'Udgave', format: 'Number', visible: false },
      { name: 'collectionName', text: 'Serietitel', visible: false },
      { name: 'group', text: 'Gruppe', tooltipText: 'Gruppe', widthType: FieldWidthEnum.S, foreignKeyType: 'productGroup' },
      { name: 'productListPrimary', fieldType: 'productList', visible: false },
      { name: 'type', text: 'Type', widthType: FieldWidthEnum.S },
      { fieldType: 'productComposition', visible: false },
      { fieldType: 'productForm', visible: false },
      { fieldType: 'productFormDetail', visible: false },
      { fieldType: 'productPublishingStatus', visible: false },
      { fieldType: 'productMarketAvailability', visible: false },
      { name: 'contractType', text: 'Kontrakttype', widthType: FieldWidthEnum.S, visible: false },
      { name: 'contractSettlementType', text: 'Afregning', widthType: FieldWidthEnum.S, visible: false },
      { name: 'subjectThema', text: 'Themakoder', widthType: FieldWidthEnum.S, visible: false },
      { name: 'subjectKeywords', text: 'Søgeord', visible: false },
      { name: 'subjectBookstoreClassification', text: 'Boghandler gruppe', widthType: FieldWidthEnum.S, visible: false, foreignKeyType: 'productCategory' },
      { name: 'salesPrice', text: 'Salgspris', format: 'Amount', widthType: FieldWidthEnum.XS, visible: false },
      { name: 'salesPriceTrade', text: 'Salgspris (BOP)', format: 'Amount', widthType: FieldWidthEnum.XS, visible: false },
      { name: 'projectLegacyId', text: 'Projekt id', widthType: FieldWidthEnum.S, visible: false },
      { name: 'created', text: 'Oprettet dato', tooltipText: 'Dato for oprettelse', format: 'DateShort', widthType: FieldWidthEnum.S, orderBy: 'Descending' },
      { name: 'isPublished', text: 'Udgivet', tooltipText: 'Er udgivet?', format: 'CheckBox', visible: false },
      { name: 'publishingDate', text: 'Udgivet dato', tooltipText: 'Dato for udgivelse', format: 'DateShort', widthType: FieldWidthEnum.S },
      { name: 'publishedDays', text: 'Dage udgivet', tooltipText: 'Dage fra udgivelse', format: 'Number', widthType: FieldWidthEnum.S, visible: false },
      { name: 'buyableTrade', text: 'Kan købes?', format: 'CheckBox', visible: false },
      { name: 'supplyApologyCode', text: 'Undskyldningskode', format: 'Integer', visible: false },
      { name: 'projectItemNum', text: 'Varenummer', visible: false },
      { name: 'deleted', text: 'Slettet', tooltipText: 'Dato for sletning', format: 'DateShort', widthType: FieldWidthEnum.S, visible: false },
      { name: 'deletedReason', text: 'Slettet årsag', widthType: FieldWidthEnum.S, visible: false },
      { name: 'isDeleted', text: 'Er slettet?', format: 'CheckBox', visible: false, filterOnClient: true, filterOperator: 'equal', filterValue: false },
      { name: 'contentDescription', text: 'Tekst', visible: false }
      ];



    if (!this.statModeApply(view, this.routeOptions)) {
      this.allModeApply(view);
    };
    view.contextMenuCommands = [uiCmdSyncSelected];
    return view;
  }

  private allModeApply(v: CerFormView) {
    v.fieldMetadata = v.fieldMetadata.concat([
      { name: 'supplyDate', text: 'Endelig tilmelding købsdato', format: 'DateShort', visible: false },
      { name: 'publishingDateFirstPublished', text: 'Første udgave', format: 'DateYear', visible: false },
      { name: 'publishingDateOutOfPrint', text: 'Udsolgt dato', format: 'DateShort', visible: false },
      { name: 'publishingLatestReprintNumber', text: 'Oplag', format: 'Number', visible: false },
      { name: 'extendPages', text: 'Sideantal', format: 'Number', visible: false },
      { name: 'publisher', text: 'Forlag', visible: false },
      { name: 'publisherImprint', text: 'Imprint', visible: false },
      { name: 'contributorTechnicalEditor', text: 'Teknisk redaktør', visible: false },
      { name: 'contributorEditor', text: 'Redaktør', visible: false },
      { name: 'contributorTranslator', text: 'Oversætter', visible: false },
      { name: 'contributorIllustrator', text: 'Illustrator', visible: false },
      { name: 'contributorPhotographer', text: 'Fotograf', visible: false },
      { name: 'contributorCover', text: 'Forside', visible: false },
      { name: 'resourceFrontCoverImageURL', text: 'Forside URL', format: 'Url', visible: false },
      { name: 'measurementHeightMM', text: 'Højde (mm)', format: 'Integer', visible: false },
      { name: 'measurementWidthMM', text: 'Bredde (mm)', format: 'Integer', visible: false },
      { name: 'measurementThicknessMM', text: 'Dybde (mm)', format: 'Integer', visible: false },
      { name: 'measurementWeightGR', text: 'Vægt (g)', format: 'Integer', visible: false },
      { name: 'sourcingMinOnhandQty', text: 'Min. lager', format: 'Integer', visible: false },
      { name: 'sourcingManager', text: 'Disponent', visible: false },
      { name: 'sourcingSupplier', text: 'Trykkeri', visible: false },
      { name: 'salesDiscountCode', text: 'Rabatkode', visible: false },
    ]);
  }

  private statModeApply(v: CerFormView, options: CerFormRouteOptions): boolean {
    const p = options.routeParams;
    const statMode: StatMode =
      p?.includes("stat") ? 'RefDataProductStat' :
        p?.includes("statCategory") ? 'RefDataProductStatCategory' :
          p?.includes("statPeriod") ? 'RefDataProductStatPeriod' :
            null;

    if (statMode == null) {
      return false;
    }

    const filterNum = options.navigation?.columnNameChild == 'productNum';
    var title = 'Nøgletal';
    if (statMode == 'RefDataProductStatCategory') {
      title += ' pr. kategori';
    }
    if (statMode == 'RefDataProductStatPeriod') {
      title += ' perioder';
    }
    v.viewMetadata.dataApiName = statMode + (filterNum ? '' : 'Product');
    v.viewMetadata.text = title;
    v.viewMetadata.textSingular = v.viewMetadata.text;
    v.title = v.viewMetadata.text;
    v.viewMetadata.primaryKey = statMode == 'RefDataProductStat' ? 'productNum' : 'id';
    v.fieldMetadata.find(f => f.name == 'num').name = 'productNum';
    if (filterNum) {
      const keep = ['id', 'productNum'];
      v.fieldMetadata = v.fieldMetadata.filter(f => keep.includes(f.name));
      v.fieldMetadata.forEach(f => f.visible = false);
    }
    else {
      const hide = ['originalTitle'];
      v.fieldMetadata.filter(f => hide.includes(f.name)).forEach(f => f.visible = false);
    }
    var fields: FieldMetadata[] = [];
    if (statMode == 'RefDataProductStat') {
      fields = [
        { name: 'salesQty', text: 'Salg antal', format: 'Number' },
        { name: 'salesAmount', text: 'Omsætning', format: 'Amount' },
        { name: 'costAmount', text: 'Omkostninger', format: 'Amount' },
        { name: 'costAmountBalance', text: 'Omk (balance)', format: 'Amount', visible: false },
        { name: 'resultAmount', text: 'Avance', format: 'Amount' },
        { name: 'salesMaxDate', text: 'Seneste salg', format: 'DateShort' },
        { name: 'costMaxDate', text: 'Seneste omk', format: 'DateShort' },
        { name: 'salesQty3Mth', text: 'Salg antal (3 mdr)', format: 'Number' },
        { name: 'salesAmount3Mth', text: 'Salg (3 mdr)', format: 'Amount', visible: false },
        { name: 'costAmount3Mth', text: 'Omk (3 mdr)', format: 'Amount', visible: false },
        { name: 'resultAmount3Mth', text: 'Avance (3 mdr)', format: 'Amount', visible: false },
        { name: 'days3Mth', text: 'Dage (3 mdr)', format: 'Number', visible: false },
        { name: 'onhandQtyAvailable', text: 'Disponibelt antal', format: 'Number' },
        { name: 'onhandQtyRemain', text: 'Fysisk lager', format: 'Number', visible: false },
        { name: 'purchQty', text: 'Købt antal', format: 'Number' },
        { name: 'costPriceEstimate', text: 'Kostpris (est.)', format: 'Amount' },
        { name: 'onhandAmountEstimate', text: 'Lager købsværdi (est.)', format: 'Amount' }
      ];
    }
    else {
      v.fieldMetadata.filter(f => f.orderBy != null).forEach(f => f.orderBy = null);
      if (statMode == 'RefDataProductStatPeriod') {
        fields = [
          { name: 'fromDate', text: 'Fra dato', format: 'DateShort', visible: false },
          { name: 'toDate', text: 'Til dato', format: 'DateMonthYear', visible: true, orderBy: 'Descending', orderByPriority: 1 },
          //          { name: 'period', text: 'Periode', format: 'DateMonthYear', orderBy: 'Descending' }
        ];
      }
      fields = fields.concat([
        { name: 'category', text: 'Art', foreignKeyType: 'financeProjectCostType', orderBy: 'Ascending', orderByPriority: 2 },
        { name: 'qty', text: 'Antal salg', format: 'Number' },
        { name: 'amount', text: 'Beløb', format: 'Amount' },
        { name: 'amountBalance', text: 'Beløb (balance)', format: 'Amount', visible: false },
        { name: 'maxDate', text: 'Seneste', format: 'DateShort', visible: (statMode == 'RefDataProductStatCategory') },
        { name: 'isSales', text: 'Salg?', format: 'CheckBox' }
      ]);
      if (filterNum) {
        const sumFields = ['qty', 'amount', 'amountBalance'];
        fields.filter(f => sumFields.includes(f.name)).forEach(f => f.footerType = 'Sum');
      }
    }
    v.fieldMetadata = v.fieldMetadata.concat(fields);

    const navPosting = v.viewMetadata.navigations.find(n => n.foreignKey.parentEntity == 'VoucherPostingProject');
    if (navPosting) {
      navPosting.foreignKey.parentColumn = 'productNum';
      navPosting.foreignKey.column = 'num';
    }
    v.viewMetadata.navigations = v.viewMetadata.navigations.filter(n => n.foreignKey.parentEntity != v.viewMetadata.dataApiName);
    v.viewMetadata.navigations = v.viewMetadata.navigations.filter(n => n.foreignKey.column == null || n.foreignKey.column == 'num');
    v.viewMetadata.navigations.forEach(n => n.foreignKey.column = 'productNum');

    return true;
  }

  public onCommand(event: UiCommandEvent) {
    switch (event.commandId) {
      case uiCmdSyncSelected.id:
        this.productService.syncFromProductAllList(event.appComponent, [event.rowData]);
        break;
    }
  }
}
